/* oxlint-disable no-inner-declarations */
import { UTCDate } from '@date-fns/utc';
import * as rq from '@tanstack/react-query';
import { format } from 'date-fns';

import { useDexFetchClient } from '@/api/looker/utils/use-dex-fetch-client';
import type { ChartData } from '@/data-explorer/entities/chart-data';
import { ChartType } from '@/data-explorer/entities/chart-type';
import type { RequestFilters } from '@/data-explorer/entities/filters';
import type { Node } from '@/data-explorer/entities/node';
import { useMe } from '@/hooks/use-me';
import { objectEntries } from '@/utils/ts/object-entries';

const categoryFromChartType: Record<ChartType, string> = {
  [ChartType.CELL_VOLTAGE]: 'cell_voltage_chart',
  [ChartType.CURRENT]: 'current_chart',
  [ChartType.SOC]: 'soc_chart',
  [ChartType.TEMPERATURE]: 'temperature_chart',
  [ChartType.VOLTAGE]: 'voltage_chart',
};

export namespace GetChartData {
  export const key = (input: Input) => [
    'data-explorer',
    'get-chart-data',
    input.node.id,
    input.from,
    input.to,
    input.filterNoise,
    Object.fromEntries(input.chartTypes.map((chartType) => [chartType, true])),
  ];

  export type QueryParams = {
    enabled?: boolean;
    retry?: number | boolean;
  };

  export type Input = {
    node: Node;
    chartTypes: ChartType[];
    from: Date;
    to: Date;
    filterNoise: boolean;
  };

  export type Output = Record<ChartType, ChartData[]>;

  export type QueryInput = RequestFilters | undefined;
  export type QueryOutput = {
    node: Node;
    query: rq.UseBaseQueryResult<Output>;
  }[];

  type FetchColumnsResult = { message: string } | Record<string, string[]>;
  type FetchSensorsResult = Record<
    string,
    Record<string, [number, number][] | undefined> | undefined
  >;

  export function useQuery(input: QueryInput, opts?: QueryParams): QueryOutput {
    const inputs: Input[] = input
      ? input.nodes.map<Input>((node) => ({
          node,
          chartTypes: input.chartTypes,
          filterNoise: input.filterNoise,
          from: input.from,
          to: input.to,
        }))
      : [];

    const queries = useQueries(inputs, {
      enabled: Boolean(input),
      ...opts,
    });

    return inputs.map(({ node }, index) => ({ node, query: queries[index] }));
  }

  export function useQueries(
    inputs: Input[],
    { retry, enabled }: QueryParams = {},
  ) {
    const fetchClient = useDexFetchClient();
    const me = useMe();

    return rq.useQueries<Output[]>({
      queries: inputs.map((input) => ({
        enabled,
        retry,
        queryKey: key(input),
        async queryFn({ signal }) {
          const columnCategories = input.chartTypes.map(
            (chartType) => categoryFromChartType[chartType],
          );

          const columnsResult = await fetchClient<FetchColumnsResult>({
            path: `/v1/columns/${me.tenant}/${input.node.level}/${input.node.id}`,
            params: {
              'data-source': input.node.source,
            },
            signal,
          });

          if ('message' in columnsResult) {
            console.error(columnsResult.message);

            throw new Error(
              `No data available for the selected node[id=${input.node.id}]`,
            );
          }

          const columnNames: string[] = Object.entries(columnsResult)
            .filter(([category]) => columnCategories.includes(category))
            .flatMap(([, n]) => n);

          const sensorsResult = await fetchClient<FetchSensorsResult>({
            path: `/v1/sensors/${me.tenant}/${input.node.level}/${input.node.id}`,
            params: {
              'data-source': input.node.source,
              'column-names': columnNames,
              'start-date': format(
                new UTCDate(input.from),
                "yyyy-MM-dd'T'HH:mm:ss",
              ),
              'end-date': format(
                new UTCDate(input.to),
                "yyyy-MM-dd'T'HH:mm:ss",
              ),
              'max-points': 500,
              'noise-filter': input.filterNoise ? 'True' : 'False',
            },
            signal,
          });

          const output: Output = {
            CellVoltage: [],
            Current: [],
            SOC: [],
            Temperature: [],
            Voltage: [],
          };

          for (const chartType of Object.values(ChartType)) {
            const categoryName = categoryFromChartType[chartType];
            const columns = sensorsResult[categoryName];

            if (!columns) {
              continue;
            }

            for (const [column, data] of objectEntries(columns)) {
              if (!data || data.length === 0) {
                continue;
              }

              output[chartType].push({ column, data });
            }
          }

          return output;
        },
      })),
    }) as rq.UseQueryResult<Output>[];
  }
}
